// BuilderTutorialOverlay - ビルダー初回ガイド（spec.md §5.8.6）
// 5ステップのスポットライト + まなパルくん吹き出し。
// 進捗保存は API スタブ + localStorage フラグ。

import { useState } from "react";
import { useSelector } from "react-redux";
import { Dialog } from "@mui/material";
import { RootState } from "app/store";
import I18n from "common/i18n";
import { CloseBtn, FadeTransition } from "common/elements";
import { useScale, usePlaySound, btnDelay } from "common/utils";
import { saveTutorialProgress } from "features/builder/api";

export const TUTORIAL_DONE_KEY = "piece_builder_tutorial_done";

// 1280x960 基準のスポットライト矩形（モック modal-tutorial.html 準拠）
type Spotlight = { top: number; left: number; width: number; height: number };

const STEPS: { titleKey: string; descKey: string; spotlight: Spotlight }[] = [
  {
    titleKey: "MSG_BUILDER_TUTORIAL_STEP1_TITLE",
    descKey: "MSG_BUILDER_TUTORIAL_STEP1_DESC",
    spotlight: { top: 820, left: 470, width: 90, height: 90 },
  },
  {
    titleKey: "MSG_BUILDER_TUTORIAL_STEP2_TITLE",
    descKey: "MSG_BUILDER_TUTORIAL_STEP2_DESC",
    spotlight: { top: 820, left: 570, width: 90, height: 90 },
  },
  {
    titleKey: "MSG_BUILDER_TUTORIAL_STEP3_TITLE",
    descKey: "MSG_BUILDER_TUTORIAL_STEP3_DESC",
    spotlight: { top: 300, left: 924, width: 340, height: 360 },
  },
  {
    titleKey: "MSG_BUILDER_TUTORIAL_STEP4_TITLE",
    descKey: "MSG_BUILDER_TUTORIAL_STEP4_DESC",
    spotlight: { top: 20, left: 700, width: 140, height: 70 },
  },
  {
    titleKey: "MSG_BUILDER_TUTORIAL_STEP5_TITLE",
    descKey: "MSG_BUILDER_TUTORIAL_STEP5_DESC",
    spotlight: { top: 90, left: 924, width: 340, height: 200 },
  },
];

export const BuilderTutorialOverlay = ({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) => {
  const play = usePlaySound();
  const { scale } = useScale();
  const active = useSelector((state: RootState) => state.user.active);

  const [step, setStep] = useState(0);

  if (!open) return null;

  const current = STEPS[step];
  const isLast = step === STEPS.length - 1;

  const finish = () => {
    window.localStorage.setItem(TUTORIAL_DONE_KEY, "1");
    saveTutorialProgress({ uid: active.uid, step_id: STEPS.length });
    setStep(0);
    onClose();
  };

  const handleNext = () => {
    play();
    btnDelay(() => {
      saveTutorialProgress({ uid: active.uid, step_id: step + 1 });
      if (isLast) {
        finish();
      } else {
        setStep(step + 1);
      }
    });
  };

  const handleSkip = () => {
    play();
    btnDelay(finish);
  };

  return (
    <Dialog
      open
      fullScreen
      maxWidth={false}
      sx={{ zIndex: 100000 }}
      TransitionComponent={FadeTransition}
      PaperProps={{ style: { backgroundColor: "transparent" } }}
    >
      <div className="flex-col-el w-full h-full relative">
        {/* 暗幕 + スポットライト切り抜き */}
        <div
          className="absolute inset-0"
          style={{
            background: "rgba(0,0,0,0.7)",
            clipPath: `polygon(
              0 0, 100% 0, 100% 100%, 0 100%, 0 0,
              ${current.spotlight.left * scale}px ${current.spotlight.top * scale}px,
              ${current.spotlight.left * scale}px ${(current.spotlight.top + current.spotlight.height) * scale}px,
              ${(current.spotlight.left + current.spotlight.width) * scale}px ${(current.spotlight.top + current.spotlight.height) * scale}px,
              ${(current.spotlight.left + current.spotlight.width) * scale}px ${current.spotlight.top * scale}px,
              ${current.spotlight.left * scale}px ${current.spotlight.top * scale}px
            )`,
          }}
        />
        <div
          className="absolute rounded-2xl border-4 border-yellow-300"
          style={{
            top: current.spotlight.top * scale,
            left: current.spotlight.left * scale,
            width: current.spotlight.width * scale,
            height: current.spotlight.height * scale,
          }}
        />

        {/* 右上 閉じる */}
        <div className="flex-col-el absolute top-3 right-3">
          <CloseBtn close={handleSkip} />
        </div>

        {/* まなパルくん吹き出し（中央下） */}
        <div className="flex-col-el flex-center absolute inset-x-0 bottom-0">
          <div
            className="flex-col-el items-stretch origin-bottom bg-white rounded-2xl shadow-2xl p-6 gap-3"
            style={{ width: 560, transform: `scale(${scale})`, marginBottom: 40 * scale }}
          >
            <div className="flex-row-el items-center gap-3">
              {/* TODO(piece-builder): features/assistant の SVG キャラへ差し替え */}
              <span className="type-display">🐥</span>
              <span className="type-title text-textcolor-black">{I18n.t(current.titleKey)}</span>
            </div>
            <span className="type-body text-textcolor-black">{I18n.t(current.descKey)}</span>

            {/* ステップドット */}
            <div className="flex-row-el flex-center gap-2">
              {STEPS.map((_, i) => (
                <div
                  key={i}
                  className={`w-3 h-3 rounded-full ${
                    i === step ? "bg-emerald-500" : "bg-gray-300"
                  }`}
                />
              ))}
            </div>

            <div className="flex-row-el justify-between items-center">
              <button
                onClick={handleSkip}
                className="opacity-button type-inherit-font type-sm text-gray-500"
              >
                {I18n.t("MSG_BUILDER_TUTORIAL_SKIP")}
              </button>
              <div className="alpha">
                <button
                  onClick={handleNext}
                  className="btn btn-primary type-inherit-font type-body"
                  style={{ margin: 0 }}
                >
                  {isLast
                    ? I18n.t("MSG_BUILDER_TUTORIAL_DONE")
                    : I18n.t("MSG_BUILDER_TUTORIAL_NEXT")}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Dialog>
  );
};
